// /src/pages/NotFoundPage.jsx
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Wrench, BookOpen, FileText, Calendar, ArrowRight } from 'lucide-react';
import { cardCls, headingCls, bodyText, mutedText, ctaPrimary, pageHeading, sectionLabel, pageWrap, sectionInner } from '../components/styles';

const destinations = [
  { to: '/', label: 'Home', icon: Home, blurb: 'Start over from the front page.' },
  { to: '/tools', label: 'Platforms', icon: Wrench, blurb: 'HADES, VitalChain, and the rest of the flagship builds.' },
  { to: '/playbooks', label: 'Playbooks', icon: BookOpen, blurb: 'Free copy-paste security runbooks, PDF and Markdown.' },
  { to: '/blog', label: 'Blog', icon: FileText, blurb: 'Writing on security, healthcare, and building things.' },
  { to: '/contact', label: 'Book Me', icon: Calendar, blurb: 'Schedule a consultation.' }
];

export default function NotFoundPage() {
  const location = useLocation();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = '404 Not Found - Domenic Laurenzi';

    // public/index.html already ships a robots meta, so appending a second one
    // would leave two conflicting directives in the head. When a tag is already
    // there, flip it and restore it on unmount. Only remove a tag we created.
    const existing = document.querySelector('meta[name="robots"]');
    const previousContent = existing ? existing.getAttribute('content') : null;
    let created = null;

    if (existing) {
      existing.setAttribute('content', 'noindex');
    } else {
      created = document.createElement('meta');
      created.setAttribute('name', 'robots');
      created.setAttribute('content', 'noindex');
      document.head.appendChild(created);
    }

    return () => {
      document.title = previousTitle;
      if (created) {
        if (created.parentNode) created.parentNode.removeChild(created);
      } else if (existing) {
        if (previousContent === null) existing.removeAttribute('content');
        else existing.setAttribute('content', previousContent);
      }
    };
  }, []);

  return (
    <div className={pageWrap}>
      {/* Hero */}
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl animate-stagger">
            <h1 className={`${pageHeading} mb-4`}><span className="opacity-50">$</span> 404</h1>
            <p className={`text-xl ${mutedText} max-w-3xl`}>
              No route matches that URL. The page was moved, renamed, or never existed.
            </p>
            <p className={`font-mono text-sm ${mutedText} mt-4 break-all`}>
              {'// requested: '}
              <span className="text-[var(--text)]">{location.pathname}</span>
            </p>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Destinations */}
      <section className="py-10" style={{ background: 'var(--bg2)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl animate-stagger">
            <p className={`${sectionLabel} mb-6`}>Try one of these</p>

            <div className="grid md:grid-cols-2 gap-6">
              {destinations.map(({ to, label, icon: Icon, blurb }) => (
                <Link
                  key={to}
                  to={to}
                  className={`${cardCls} p-6 flex items-start gap-4 hover:translate-y-[-2px] transition`}
                >
                  <span
                    className="p-2 rounded-lg flex-shrink-0 border border-[color:var(--border)] bg-[var(--bg2)] text-[var(--accent)]"
                    aria-hidden="true"
                  >
                    <Icon size={20} />
                  </span>
                  <span>
                    <span className={`block text-xl ${headingCls} mb-1`}>{label}</span>
                    <span className={`block ${bodyText} leading-relaxed`}>{blurb}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* CTA */}
      <section className="py-10" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <p className={`${bodyText} mb-6`}>
              If you followed a link here from somewhere on this site, that is a bug worth reporting.
            </p>
            <Link to="/contact" className={ctaPrimary}>
              Tell me about it <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
